/**
 * @fileoverview Hook for fetching bookmarked notes with transformation and sorting.
 * Wraps the raw React Query hook to provide a cleaner API for bookmark UI components.
 */

import { useBookmarkedNotesQuery } from './useNotes';
import { sortByUpdatedAtDesc } from '../utils/noteUtils';
import type { BookmarkedNote, NotesWithTags, UseBookmarkedNotesReturn } from '../types';

// Re-export type for consumers
export type { BookmarkedNote };

/** Type guard ensuring a note has the non-null fields required to render as a bookmark */
function hasRequiredBookmarkFields(
  note: NotesWithTags
): note is NotesWithTags & { id: string; content: string; created_at: string; updated_at: string } {
  return (
    typeof note.id === 'string' &&
    typeof note.content === 'string' &&
    typeof note.created_at === 'string' &&
    typeof note.updated_at === 'string'
  );
}

/** Transform a note with the required fields to BookmarkedNote format */
function toBookmarkedNote(
  note: NotesWithTags & { id: string; content: string; created_at: string; updated_at: string }
): BookmarkedNote {
  return {
    id: note.id,
    title: note.title,
    content: note.content,
    created_at: note.created_at,
    updated_at: note.updated_at,
  };
}

/**
 * Hook for fetching and transforming bookmarked notes.
 * Returns a simpler structure suitable for bookmark UI components.
 */
export function useBookmarkedNotes(): UseBookmarkedNotesReturn {
  const { data: notes, isLoading, error, refetch } = useBookmarkedNotesQuery();

  // Transform to simpler structure, filter out notes without timestamps, and sort by most recent
  const transformedNotes = (notes ?? [])
    .filter(hasRequiredBookmarkFields)
    .map(toBookmarkedNote);
  
  const bookmarkedNotes: BookmarkedNote[] = sortByUpdatedAtDesc(transformedNotes);

  return {
    bookmarkedNotes,
    loading: isLoading,
    error: error instanceof Error ? error : error ? new Error('Failed to fetch bookmarked notes') : null,
    refetch,
  };
}